import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/ui/components/shadcn/dialog';
import { Button } from '@/shared/ui/components/shadcn/button';
import { Anchor, SegmentedControl, Text, TextInput } from '@/shared/ui';
import { VistoryService } from '../services/vistory-service';
import type { CodeSource } from '../types/vistory.types';

interface Props {
  agencyId: string;
  vistoryId: string;
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Dialog that captures code-source and 4-digit identifier to send a vistory
 * for tenant approval, then shows the returned share links.
 */
export function SendApprovalDialog({ agencyId, vistoryId, open, onClose, onSent }: Props) {
  const { t } = useTranslation('features/vistories');
  const [codeSource, setCodeSource] = useState<CodeSource>('cpf_first_4');
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [shareLinks, setShareLinks] = useState<{ public_link: string; whatsapp_url: string } | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const codeSourceOptions = [
    { label: t('vistory.code_source.cpf'), value: 'cpf_first_4' as const },
    { label: t('vistory.code_source.phone'), value: 'phone_last_4' as const },
  ];

  const identifierLabel = codeSource === 'cpf_first_4'
    ? t('vistory.identifier_cpf')
    : t('vistory.identifier_phone');

  const canSend = identifier.trim().length === 4;

  async function handleSend() {
    if (!canSend) return;
    setSending(true);
    setError('');
    try {
      await VistoryService.sendForApproval(agencyId, vistoryId, {
        code_source: codeSource,
        client_identifier: identifier.trim(),
      });
      const links = await VistoryService.shareVistory(agencyId, vistoryId, {
        channel: 'email',
        to: email || 'noreply@housien.com.br',
      });
      setShareLinks(links);
      onSent();
    } catch {
      setError(t('vistory.send_error'));
    } finally {
      setSending(false);
    }
  }

  function handleCopy() {
    if (shareLinks) {
      navigator.clipboard.writeText(shareLinks.public_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  if (shareLinks) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('vistory.share_title')}</DialogTitle>
          </DialogHeader>
          <ShareLinksView
            publicLink={shareLinks.public_link}
            whatsappUrl={shareLinks.whatsapp_url}
            copied={copied}
            onCopy={handleCopy}
            email={email}
            setEmail={setEmail}
            agencyId={agencyId}
            vistoryId={vistoryId}
            t={t}
          />
          <DialogFooter>
            <Button onClick={onClose}>{t('vistory.done')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('vistory.send_approval_title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-[var(--space-4)]">
          <div className="flex flex-col gap-[var(--space-1)]">
            <Text span size="xs" fw={500} className="text-[var(--text-secondary)]">
              {t('vistory.code_source_label')}
            </Text>
            <SegmentedControl
              items={codeSourceOptions}
              value={codeSource}
              onChange={(v) => setCodeSource(v as CodeSource)}
            />
          </div>
          <TextInput
            label={identifierLabel}
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="0000"
            maxLength={4}
            inputMode="numeric"
          />
          <TextInput
            label={t('vistory.share_email_label')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('vistory.share_email_placeholder')}
            type="email"
          />
          {error && (
            <Text span size="xs" className="text-[var(--status-error)]">{error}</Text>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending}>
            {t('vistory.cancel')}
          </Button>
          <Button onClick={handleSend} disabled={!canSend || sending}>
            {t('vistory.send_approval')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * View with copy/WhatsApp/email actions for sharing a vistory approval link.
 */
function ShareLinksView({ publicLink, whatsappUrl, copied, onCopy, email, setEmail, agencyId, vistoryId, t }: {
  publicLink: string; whatsappUrl: string; copied: boolean; onCopy: () => void;
  email: string; setEmail: (v: string) => void;
  agencyId: string; vistoryId: string; t: (k: string) => string;
}) {
  const [sharing, setSharing] = useState(false);

  async function handleEmailShare() {
    if (!email) return;
    setSharing(true);
    try {
      await VistoryService.shareVistory(agencyId, vistoryId, { channel: 'email', to: email });
    } finally { setSharing(false); }
  }

  return (
    <div className="flex flex-col gap-[var(--space-3)]">
      <div className="rounded-(--radius-md) bg-[var(--surface-secondary)] p-[var(--space-3)] flex items-center gap-[var(--space-2)]">
        <Text size="xs" className="flex-1 text-[var(--text-primary)] truncate">{publicLink}</Text>
        <Button variant="outline" size="sm" onClick={onCopy}>
          {copied ? t('vistory.copied') : t('vistory.copy_link')}
        </Button>
      </div>
      <Button variant="outline" asChild>
        <Anchor href={whatsappUrl} target="_blank" rel="noopener noreferrer" underline="never">
          {t('vistory.share_whatsapp')}
        </Anchor>
      </Button>
      <div className="flex gap-[var(--space-2)]">
        <TextInput
          className="flex-1"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('vistory.share_email_placeholder')}
          type="email"
        />
        <Button variant="outline" onClick={handleEmailShare} disabled={!email || sharing}>
          {t('vistory.share_email')}
        </Button>
      </div>
    </div>
  );
}
